import type { TaskCompletionInvalidationScope } from "@remi/enums/task/completion-invalidation-scope";
import type { EqualWithDiff, Expect } from "@remi/ts-utils";
import { assertNever, isOneOf } from "@remi/ts-utils";
import type { CacheMessageForType, CacheMessageType, CompletionPhase } from "@remi/types/socket/message";

export type CompletionTrackerPhase = "pending" | "delayed" | "committed" | "evaluated";

// Ranked, not a flat enum: transitions compare ranks, so a duplicate publish (an Inngest step replay
// after a lost ack) or an out-of-order one is mechanically a no-op.
export const PHASE_RANK = {
  pending: 0,
  delayed: 1,
  committed: 2,
  evaluated: 3,
} as const satisfies Record<CompletionTrackerPhase, number>;

export type CompletionTerminalKind = "failed" | "raceLost" | "aborted";

// Sibling completions on one project settle within milliseconds of each other under the bid-scoped
// concurrency key, so running the set once per window costs a fraction of the queries.
export const INVALIDATION_COALESCE_WINDOW_MS = 250;

export const shouldCoalesceInvalidation = (previousAt: number | undefined, now: number): boolean =>
  previousAt !== undefined && now - previousAt < INVALIDATION_COALESCE_WINDOW_MS;

export type InvalidationScopes = readonly TaskCompletionInvalidationScope[] | undefined;

export const getScopesToRun = (lastRan: InvalidationScopes, requested: InvalidationScopes): InvalidationScopes => {
  if (requested === undefined) {
    return undefined;
  }
  if (lastRan === undefined) {
    return [];
  }
  return requested.filter((scope) => !lastRan.includes(scope));
};

export const mergeInvalidationScopes = (left: InvalidationScopes, right: InvalidationScopes): InvalidationScopes =>
  left === undefined || right === undefined ? undefined : [...new Set([...left, ...right])];

// Strip query/hash before comparing: both sides come from `router.asPath`, and a shallow push that only
// changes the query (e.g. closing the drawer) must not read as having left the page.
const pathnameOf = (path: string): string => path.split(/[?#]/, 1)[0] ?? path;

// Yanking a user off an unrelated page 20-40s after they asked is worse than not navigating at all.
export const shouldRunNavigationIntent = (registeredPathname: string, currentPathname: string): boolean =>
  pathnameOf(registeredPathname) === pathnameOf(currentPathname);

// Below `committed` the server has not confirmed anything, while the optimistic write already shows the
// task as completed — that gap is what the pending affordance exists to show.
export const isCompletionPending = (entries: readonly { phase: CompletionTrackerPhase }[]): boolean =>
  entries.some((entry) => PHASE_RANK[entry.phase] < PHASE_RANK.committed);

// Attempt-tracker vs completion-tracker precedence: CompletionTracker's phases are real server-confirmed
// CAS results, while AttemptTracker's pending state precedes any commit — so when a separate completion
// path races an in-flight attempt on the same task, completion pending wins.
export const shouldSuppressAttemptPending = (isTaskCompletionPending: boolean): boolean => isTaskCompletionPending;

type CompletionMessageClass = { kind: "ordinary" } | { kind: CompletionPhase };

const WIRE_COMPLETION_PHASES = [
  "committed",
  "evaluated",
  "failed",
  "raceLost",
  "aborted",
] as const satisfies readonly CompletionPhase[];

// `satisfies` alone is a subset assertion, so a sixth CompletionPhase member would pass it and then be
// degraded to `ordinary` by the runtime branch below, stranding the entry until its TTL. Exact equality
// catches OUR additions at build time; the runtime degradation is for a newer SERVER's.
type _WireCompletionPhasesExact = Expect<EqualWithDiff<(typeof WIRE_COMPLETION_PHASES)[number], CompletionPhase>>;

export const classifyCompletionMessage = (
  message: CacheMessageForType<typeof CacheMessageType.PROJECT_TASKS>,
): CompletionMessageClass => {
  if (message.completionNonce === undefined || message.completionPhase === undefined) {
    return { kind: "ordinary" };
  }
  // An unrecognized phase degrades to ordinary rather than failing closed: the wire field is a loose
  // string so a future phase from a newer server does not strand this client on a wrong terminal.
  if (!isOneOf(message.completionPhase, WIRE_COMPLETION_PHASES)) {
    return { kind: "ordinary" };
  }
  return { kind: message.completionPhase };
};

export const COMPLETION_UNCONFIRMED = "We couldn't confirm that completion. Refreshing this task.";

// `failed` after `committed` means the task IS durably complete and a follow-up step is not, so the
// copy must not read as a failed completion and the optimistic write must not be reverted.
export const COMPLETION_TAIL_FAILED = "Task completed. A follow-up step failed — repair pending.";

export const COMPLETION_ABORTED = "That task changed before it completed — refreshing.";

// `aborted` after `committed` is a superseded tail: the commit is durable, so do not revert, and
// the copy must not read as a failed completion.
export const COMPLETION_SUPERSEDED = "Task completed. Follow-up work was replaced — refreshing.";

interface CompletionTerminalDisposition {
  revert: boolean;
  toast: { title: string; variant: "error" | "warning" } | null;
}

// `raceLost` is deliberately silent: another durable run owns the completion, so the user has
// nothing to act on.
export const getTerminalDisposition = (
  phase: CompletionTrackerPhase,
  kind: CompletionTerminalKind,
): CompletionTerminalDisposition => {
  const observedCommitted = PHASE_RANK[phase] >= PHASE_RANK.committed;

  switch (kind) {
    case "failed":
      return observedCommitted
        ? { revert: false, toast: { title: COMPLETION_TAIL_FAILED, variant: "warning" } }
        : { revert: true, toast: { title: COMPLETION_UNCONFIRMED, variant: "error" } };
    case "aborted":
      return observedCommitted
        ? { revert: false, toast: { title: COMPLETION_SUPERSEDED, variant: "warning" } }
        : { revert: true, toast: { title: COMPLETION_ABORTED, variant: "error" } };
    case "raceLost":
      return { revert: true, toast: null };
    default:
      return assertNever(kind, `unhandled completion terminal kind: ${String(kind)}`);
  }
};
